import { LoadScript } from '@react-google-maps/api';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { IonButton, IonInput, IonItem, IonLabel, IonList } from '@ionic/react';
import { useStoreState } from 'pullstate';
import poweredByGoogle from '../../../images/powered_by_google.png';
import {
    addressFormStore,
    profileFormStore,
    toggleStore,
    userFormStore,
} from '../../../store/Store';
import '../../../theme/FlowModal.css';

/**
 * Split a Google Places label ("street, city, province, country") into the
 * three manual fields, and remember the full label as the user's address.
 * The comma handling is unchanged from the original forms: it lets a trailing
 * comma be typed in the manual fields without being swallowed.
 */
export function applyAddressLabel(label) {
    userFormStore.update((s) => {
        s.detailUserForm = { ...s.detailUserForm, address: label };
    });

    const result = label.split(',');
    addressFormStore.update((s) => {
        let { address, city, province } = s.addressForm;
        if (result[0] === '') {
            address = '';
            city = '';
            province = '';
        } else if (result[1] === '') {
            city = '';
            province = '';
        } else if (result[2] === '') {
            province = '';
        }
        if (result[0]) {
            address = `${result[0]}`;
        }
        if (result[0] && result[1] !== undefined) {
            address = `${result[0]},`;
        }
        if (result[1]) {
            city = `${result[1]}`;
        }
        if (result[1] && result[2] !== undefined) {
            city = `${result[1]},`;
        }
        if (result[2]) {
            province = `${result[2]}`;
        }
        s.addressForm = { address, city, province };
    });
}

/** Join the manual fields into one line, the way the forms always have. */
export function composeManualAddress(addressForm) {
    const address = addressForm.address || '';
    const city = addressForm.city || '';
    const province = addressForm.province || '';
    const formAddress = address[address.length - 1] === ',' ? address : `${address}, `;
    const formCity = city[city.length - 1] === ',' ? city : `${city}, `;
    return `${formAddress}${formCity}${province}`;
}

/** True when all three manual fields have something in them. */
export function isManualAddressComplete(addressForm) {
    return Boolean(addressForm.address && addressForm.city && addressForm.province);
}

/**
 * Address entry shared by the permit and account flows: a Google Places
 * search by default, with a switch to three plain fields for addresses the
 * search does not list. State lives in the shared stores so the confirming
 * step can read it.
 */
function AddressPicker({ idPrefix = 'address' }) {
    const addressForm = addressFormStore.useState((s) => s.addressForm);
    const detailUserForm = userFormStore.useState((s) => s.detailUserForm);
    const manualAddress = toggleStore.useState((s) => s.manualAddress);
    const { mapsAPI, settingsForm } = useStoreState(profileFormStore);
    const bounds = settingsForm && settingsForm.boundCoord;

    const setManual = (value) => {
        toggleStore.update((s) => {
            s.manualAddress = value;
        });
    };

    const updateField = (field, value) => {
        addressFormStore.update((s) => {
            s.addressForm = { ...s.addressForm, [field]: value };
        });
    };

    if (manualAddress) {
        return (
            <>
                <IonList lines='full'>
                    <IonItem>
                        <IonLabel position='stacked'>Street address</IonLabel>
                        <IonInput
                            id={`${idPrefix}-street`}
                            name='street-address'
                            autocomplete='street-address'
                            value={addressForm.address}
                            placeholder='Enter address'
                            required
                            onIonChange={(e) => updateField('address', e.detail.value || '')}
                        />
                    </IonItem>
                    <IonItem>
                        <IonLabel position='stacked'>City</IonLabel>
                        <IonInput
                            id={`${idPrefix}-city`}
                            name='city'
                            autocomplete='address-level2'
                            value={addressForm.city}
                            placeholder='Enter city'
                            required
                            onIonChange={(e) => updateField('city', e.detail.value || '')}
                        />
                    </IonItem>
                    <IonItem>
                        <IonLabel position='stacked'>Province</IonLabel>
                        <IonInput
                            id={`${idPrefix}-province`}
                            name='province'
                            autocomplete='address-level1'
                            value={addressForm.province}
                            placeholder='Enter province'
                            required
                            onIonChange={(e) => updateField('province', e.detail.value || '')}
                        />
                    </IonItem>
                </IonList>
                <IonButton className='flow-link-button' fill='clear' onClick={() => setManual(false)}>
                    Search address instead
                </IonButton>
            </>
        );
    }

    return (
        <>
            <label className='flow-field-label' htmlFor={`${idPrefix}-search`}>
                Search for your address
            </label>
            {mapsAPI && bounds ? (
                <LoadScript
                    id='script-loader'
                    googleMapsApiKey={`${mapsAPI}&libraries=drawing,places`}
                    language='en'
                    region='us'
                >
                    <div className='flow-address-search'>
                        <GooglePlacesAutocomplete
                            selectProps={{
                                inputId: `${idPrefix}-search`,
                                placeholder: 'Search Address...',
                                'aria-label': 'Search for your address',
                                menuPlacement: 'auto',
                                onChange: (option) => {
                                    if (option && option.label) {
                                        applyAddressLabel(option.label);
                                    }
                                },
                            }}
                            autocompletionRequest={{
                                bounds: [
                                    { lat: bounds.s, lng: bounds.w },
                                    { lat: bounds.n, lng: bounds.e },
                                ],
                                componentRestrictions: {
                                    country: ['ca'],
                                },
                                strictBounds: true,
                            }}
                        />
                        <img
                            className='flow-google-logo'
                            src={poweredByGoogle}
                            alt='Powered by Google'
                        />
                    </div>
                </LoadScript>
            ) : (
                <p className='flow-step__muted'>Address search is loading…</p>
            )}
            {detailUserForm.address && (
                <p className='flow-step__lead' role='status'>
                    Selected address: {detailUserForm.address}
                </p>
            )}
            <p>
                Use the search box above by entering the address you're looking for. When you see
                the correct address, please click/tap on it to set it.
            </p>
            <p className='flow-step__muted'>Don't see your address listed?</p>
            <IonButton className='flow-link-button' fill='clear' onClick={() => setManual(true)}>
                Manually enter address
            </IonButton>
        </>
    );
}

export default AddressPicker;
